import React, { useState } from 'react';
import { Shield, Lock, Mail, AlertTriangle, CheckCircle2, ClipboardList } from 'lucide-react';

export default function LoginPage({ authStatus, onLogin, waitlistOpen = false, onOpenWaitlist, onBack, backLabel = 'Back' }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const needsBootstrap = Boolean(authStatus?.needsBootstrap);

  async function handleSubmit(event) {
    event.preventDefault();
    setMessage('');
    setIsSubmitting(true);
    const result = await onLogin(email, password);
    setIsSubmitting(false);
    if (!result?.ok) setMessage(result?.message || 'Sign in failed. Please try again.');
  }

  return (
    <div className="min-h-screen bg-mentor-bg flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-5xl grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-6 items-stretch">
        <section className="glass-card p-8 lg:p-10 border border-mentor-border/60 flex flex-col justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-mentor-accent/30 bg-mentor-accent/10 px-3 py-1 text-xs font-display font-semibold uppercase tracking-widest text-mentor-accent mb-6">
              <Shield size={14} /> Secure Access + Waitlist
            </div>
            <h1 className="font-display text-4xl lg:text-5xl font-bold text-mentor-text leading-tight mb-4">
              Bond <span className="text-gradient">Mentor</span> is now protected.
            </h1>
            <p className="font-body text-mentor-muted text-base leading-relaxed max-w-2xl">
              Sign in to access Mission Control, market-risk dashboards, Replay, Backtesting, Probability, and protected admin tools. This phase includes secure login, invite-only registration, and a public waitlist request flow for early-access vetting.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-8">
            <div className="rounded-2xl border border-mentor-border/50 bg-white/[0.03] p-4">
              <p className="text-xl mb-1">🔐</p>
              <h3 className="font-display font-bold text-sm text-mentor-text">Login Required</h3>
              <p className="text-xs text-mentor-muted mt-1">Private dashboards are no longer open by default.</p>
            </div>
            <div className="rounded-2xl border border-mentor-border/50 bg-white/[0.03] p-4">
              <p className="text-xl mb-1">👥</p>
              <h3 className="font-display font-bold text-sm text-mentor-text">Roles</h3>
              <p className="text-xs text-mentor-muted mt-1">SuperAdmin, Admin, Moderator, and User roles are supported.</p>
            </div>
            <div className="rounded-2xl border border-mentor-border/50 bg-white/[0.03] p-4">
              <p className="text-xl mb-1">🛡️</p>
              <h3 className="font-display font-bold text-sm text-mentor-text">Admin Guard</h3>
              <p className="text-xs text-mentor-muted mt-1">Admin Health and reset actions require admin access.</p>
            </div>
          </div>
        </section>

        <section className="glass-card p-6 border border-mentor-border/60">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-lg shadow-blue-500/20">
              <Lock size={20} className="text-white" />
            </div>
            <div>
              <h2 className="font-display font-bold text-xl text-mentor-text">Sign In</h2>
              <p className="text-xs text-mentor-muted">Secure dashboard access</p>
            </div>
          </div>

          {needsBootstrap && (
            <div className="rounded-2xl border border-signal-yellow/40 bg-signal-yellow/10 p-4 mb-5">
              <div className="flex items-start gap-3">
                <AlertTriangle size={18} className="text-signal-yellow shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-display font-bold text-sm text-mentor-text">First owner account needed</h3>
                  <p className="text-xs text-mentor-muted leading-relaxed mt-1">
                    No users exist yet. Add AUTH_BOOTSTRAP_EMAIL and AUTH_BOOTSTRAP_PASSWORD to your .env file, then restart the Node server.
                  </p>
                </div>
              </div>
            </div>
          )}

          {!needsBootstrap && authStatus?.bootstrap?.created && (
            <div className="rounded-2xl border border-signal-green/40 bg-signal-green/10 p-4 mb-5 flex gap-3">
              <CheckCircle2 size={18} className="text-signal-green shrink-0 mt-0.5" />
              <p className="text-xs text-mentor-muted leading-relaxed">
                Your first SuperAdmin account was created from .env. Sign in with that email and password.
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <label className="block">
              <span className="text-xs font-display font-semibold text-mentor-muted uppercase tracking-wider block mb-1.5">Email</span>
              <div className="relative">
                <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-mentor-muted" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className="w-full rounded-xl border border-mentor-border/60 bg-mentor-bg/90 pl-10 pr-3 py-3 text-sm text-mentor-text focus:outline-none focus:border-mentor-accent/60"
                  placeholder="you@example.com"
                />
              </div>
            </label>

            <label className="block">
              <span className="text-xs font-display font-semibold text-mentor-muted uppercase tracking-wider block mb-1.5">Password</span>
              <div className="relative">
                <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-mentor-muted" />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="w-full rounded-xl border border-mentor-border/60 bg-mentor-bg/90 pl-10 pr-3 py-3 text-sm text-mentor-text focus:outline-none focus:border-mentor-accent/60"
                  placeholder="Enter your password"
                />
              </div>
            </label>

            {message && (
              <div className="rounded-xl border border-signal-red/40 bg-signal-red/10 p-3 text-xs text-mentor-text">
                {message}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting || needsBootstrap}
              className="w-full rounded-xl bg-mentor-accent px-4 py-3 font-display font-bold text-white shadow-lg shadow-blue-500/20 transition-all hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Signing In...' : 'Sign In to Bond Mentor'}
            </button>
          </form>


          {waitlistOpen && (
            <div className="mt-5 rounded-2xl border border-mentor-border/50 bg-white/[0.03] p-4">
              <div className="flex items-start gap-3">
                <ClipboardList size={18} className="text-mentor-accent shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-display font-bold text-sm text-mentor-text">Need early access?</h3>
                  <p className="text-xs text-mentor-muted leading-relaxed mt-1">Join the waitlist and the Bond Mentor team can review your request before sending an invite.</p>
                  <button type="button" onClick={onOpenWaitlist} className="mt-3 text-xs font-display font-bold text-mentor-accent hover:text-blue-300">Open waitlist request →</button>
                </div>
              </div>
            </div>
          )}

          {onBack && (
            <button type="button" onClick={onBack} className="mt-4 w-full text-center text-xs text-mentor-muted hover:text-mentor-text">
              ← {backLabel}
            </button>
          )}

          <p className="text-[10px] text-mentor-muted text-center mt-5 leading-relaxed">
            Bond Mentor uses invite-only secure access with waitlist vetting.
          </p>
        </section>
      </div>
    </div>
  );
}
